import React, { useContext, useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { validateInvitationToken, acceptInvitation } from '../services/RecruiterService';
import { AuthContext } from "../providers/AuthContextProvider";
import { StyleContext } from "../providers/StyleContextProvider";
import { Footer } from '../components/Shared/Footer';
import { TextInput } from '../components/Shared/Inputs/TextInput';
import { GetInTouch } from '../components/Shared/GetInTouch';
import { InputValidation } from '../models/InputValidation';
import { Button } from '../components/Shared/Buttons/Button';
import { useMediaQuery } from 'react-responsive';
import { useAlert } from '../hooks/useAlert';
import { RecruiterInvitation, InvitationResponse } from '../models/RecruiterInvitation';
import { RecruiterInvitationLoader } from '../components/Loaders/RecruiterInvitationPage';
import useErrorPage from '../hooks/useErrorPage';
import { RecruiterHeader } from '../components/Shared/RecruiterHeader';
import { PasswordValidation } from '../components/Shared/PasswordValidationPanel';
import { ReactComponent as HeaderMobile } from "../assets/svg/short-building.svg";
import { ReactComponent as HeaderDesktop } from "../assets/svg/long-bulding.svg";
import { ReactComponent as InvalidApplied } from '../assets/svg/invalid-applied.svg';
import { validatePasswordRules } from '../helpers/Common';

export const RecruiterInvitationPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invitation, setInvitation] = useState<InvitationResponse | null>(null);
  const [invalidMessage, setInvalidMessage] = useState<string>('');
  const isMobile = useMediaQuery({ query: "(max-width: 786px)" });
  const styleContext = useContext(StyleContext);
  const authContext = useContext(AuthContext);
  const [styles, setStyles] = useState<any>(styleContext.getComponentStyle("recruiterInvitation"));
  const { Alert, openAlert } = useAlert();
  const [toErrorPage] = useErrorPage();
  const [passwordFocused, setPasswordFocused] = useState<boolean>(false);
  const id = searchParams.get('id');

  const [formData, setFormData] = useState({
    forename: '',
    surname: '',
    password: '',
    confirmPassword: '',
  });

  const [validation, setValidation] = useState<Record<string, InputValidation>>({
    forename: {},
    surname: {},
    password: {},
    confirmPassword: {},
  });

  useEffect(() => {
    setStyles(styleContext.getComponentStyle("recruiterInvitation"));
  }, [isMobile]);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    const fetchInvitation = async () => {
      await validateInvitationToken(id)
        .then((response) => {
          if (response.isValid) {
            setInvitation(response);
          } else {
            setInvalidMessage(response.message);
          }
        })
        .catch((error) => { 
          toErrorPage(error);
        })
        .finally(() => setLoading(false));
    };
    fetchInvitation();
  }, [id]);

  const validateInput = (name: string, value: string, passwordValue?: string): InputValidation => {
    if (name === 'forename') {
      return value === ''
        ? { valid: false, invalid: true, invalidMessage: 'First Name is required.' }
        : { valid: true, invalid: false };
    }
    if (name === 'surname') {
      return value === ''
        ? { valid: false, invalid: true, invalidMessage: 'Last Name is required.' }
        : { valid: true, invalid: false };
    }
    if (name === 'password') {
      if (value === '') return { valid: false, invalid: true, invalidMessage: 'Password is required.' };
      if (!validatePasswordRules(value).isValid) {
        return { valid: false, invalid: true, invalidMessage: 'Password does not meet the requirements.' };
      }
      return { valid: true, invalid: false };
    }
    if (name === 'confirmPassword') {
      const password = passwordValue ?? formData.password;
      if (value === '') return { valid: false, invalid: true, invalidMessage: 'Confirm Password is required.' };
      if (password !== value) return { valid: false, invalid: true, invalidMessage: 'Passwords do not match.' };
      return { valid: true, invalid: false };
    }
    return {};
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);
    if (validation[name] && validation[name].invalid) {
      setValidation((prev) => ({ ...prev, [name]: validateInput(name, value, updatedFormData.password) }));
    }
    if (name === 'password' && updatedFormData.confirmPassword) {
      setValidation((prev) => ({
        ...prev,
        confirmPassword: validateInput('confirmPassword', updatedFormData.confirmPassword, value),
      }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setValidation((prev) => ({ ...prev, [name]: validateInput(name, value, formData.password) }));
  };

  const validateForm = (): boolean => {
    const fields = ['forename', 'surname', 'password', 'confirmPassword'] as const;
    const newValidation = { ...validation };
    let hasError = false;
    let hasMismatch = false;
    let hasOtherErrors = false;

    fields.forEach((name) => {
      const result = validateInput(name, formData[name], formData.password);
      newValidation[name] = result;
      if (result.invalid) {
        hasError = true;
        if (result.invalidMessage === 'Passwords do not match.') {
          hasMismatch = true;
        } else {
          hasOtherErrors = true;
        }
      }
    });

    setValidation(newValidation);
    if (hasError) {
      openAlert("error", hasMismatch && !hasOtherErrors ? "Passwords do not match. Please re-enter your password." : "Please fill in all required fields.");
    }
    return !hasError;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invitation) return;
    if (!validateForm()) return;
    const recruiterFormData: RecruiterInvitation = {
      guid: invitation.guid,
      password: formData.password,
      recruiter: {
        recruiterId: invitation.recruiter.recruiterId,
        organizationId: invitation.recruiter.organizationId,
        isOrganizationAdmin: invitation.recruiter.isOrganizationAdmin,
        email: invitation.recruiter.email,
        forename: formData.forename,
        surname: formData.surname,
      }
    };
    setSubmitting(true);
    await acceptInvitation(recruiterFormData)
      .then((response) => {
        if (response.status) {
          authContext.setLoggedRecruiter(response.data, response.token);
          navigate('/recruiter/vacancy');
        } else {
          openAlert("error", response.message);
        }
      })
      .catch((error) => {
        toErrorPage(error);
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <>
      <RecruiterHeader recruiterLoginEnable={true} isBackButtonHidden={true} />
      <div style={styles.header}>
        <hr style={styles.divider} />
        {isMobile ? <HeaderMobile style={styles.header.backgroundSvg} /> : <HeaderDesktop style={styles.header.backgroundSvg} />}
        <div style={styles.header.heading}>{"Complete Your Account"}</div>
      </div>
      <main className="page-content">
        {loading || submitting ? (
          <RecruiterInvitationLoader />
        ) : invalidMessage ? (
            <div style={styles.container}>
              <InvalidApplied style={styles.image}/>
              <h3 style={styles.invalidTitle}>{invalidMessage}</h3>
              <div style={styles.buttonsContainer}>
                <Button style={styles.button} onClick={() => navigate('/recruiter/account/login')}>{"LOGIN"}</Button>
                <Button style={{...styles.button, ...styles.button.back}} onClick={() => navigate('/')}>{"GO BACK TO HOME"}</Button>
              </div>
            </div>
        ) : (
          <form onSubmit={handleSubmit} autoComplete="off">
            <div style={styles.formContainer}>
              <div style={styles.nameWrapper}>
                <div style={styles.inputWrapper}>
                  <label htmlFor="forename" style={styles.inputLabel}>First Name *</label>
                  <TextInput id="forename" inputType="text" name="forename" autoComplete="off" value={formData.forename} handleChange={handleChange} handleBlur={handleBlur} validation={validation.forename} styles={styles.textInput} />                </div>
                <div style={styles.inputWrapper}>
                  <label htmlFor="surname" style={styles.inputLabel}>Last Name *</label>
                  <TextInput id="surname" inputType="text" name="surname" autoComplete="off" value={formData.surname} handleChange={handleChange} handleBlur={handleBlur} validation={validation.surname} styles={styles.textInput} />                </div>
              </div>
              <div style={styles.inputWrapper}>
                <label htmlFor="password" style={styles.inputLabel}>Password *</label>
                <div
                  style={styles.passwordField}
                  onFocus={() => setPasswordFocused(true)}
                  onBlur={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget as Node)) 
                      setPasswordFocused(false);
                  }}
                >
                  <TextInput 
                    id="password" 
                    inputType="password" 
                    name="password" 
                    autoComplete="new-password" 
                    value={formData.password} 
                    handleChange={handleChange} 
                    handleBlur={handleBlur} 
                    validation={validation.password} 
                    styles={styles.textInput} 
                  />
                  <PasswordValidation
                    password={formData.password}
                    touched={validation.password?.invalid}
                    showStrength
                    focused={passwordFocused}
                  />
                </div>
              </div>
              <div style={styles.inputWrapper}>
                <label htmlFor="confirmPassword" style={styles.inputLabel}>Confirm Password *</label>
                <TextInput id="confirmPassword" inputType="password" name="confirmPassword" autoComplete="new-password" value={formData.confirmPassword} handleChange={handleChange} handleBlur={handleBlur} validation={validation.confirmPassword} styles={styles.textInput} />              </div>
              <Button type="submit" style={styles.submitButton} disabled={submitting}>Complete Setup</Button>
            </div>
          </form>
        )}
      </main>
      <GetInTouch />
      <Footer />
      <Alert />
    </>
  );
};
